import * as React from 'react'
import { ChevronsUpDown } from 'lucide-react'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { useQuery } from '@tanstack/react-query'

import { cn } from '@/lib/utils'
import { Button } from '../../primitives/Button'
import { useDebounce } from '@/hooks/useDebounce'
import { SearchFilter } from './SearchFilter'

export interface SearchableFilterOption {
  id: string | number
  label: string
  subtitle?: string
  disabled?: boolean
}

interface SearchableFilterProps {
  /**
   * Filter title (displayed in button)
   */
  title: string
  /**
   * Button icon
   */
  icon?: React.ReactNode
  /**
   * Unique name for caching
   */
  name: string
  /**
   * Fetcher function to get options
   */
  fetcher: (_search: string) => Promise<SearchableFilterOption[]>
  /**
   * Current selected value
   */
  value?: string | number | null
  /**
   * Callback when value changes
   */
  onChange: (_value: string | number | null) => void
  /**
   * Placeholder for search input
   */
  searchPlaceholder?: string
  /**
   * Message when no results found
   */
  emptyMessage?: string
  /**
   * Popover width (default: 240px)
   */
  popoverWidth?: string
  /**
   * Hide search input (for short/fixed lists like categories)
   */
  hideSearch?: boolean
  /**
   * Popover alignment relative to trigger button
   * @default 'end' - maintains current behavior
   */
  popoverAlign?: 'start' | 'center' | 'end'
}

/**
 * SearchableFilter - Filterable with integrated search
 * Designed to use in list headers together with other newRedesign filters
 */
export function SearchableFilter({
  title,
  icon,
  name,
  fetcher,
  value,
  onChange,
  searchPlaceholder = 'Buscar...',
  emptyMessage = 'No se encontraron resultados',
  popoverWidth = '280px',
  hideSearch = false,
  popoverAlign = 'end',
}: SearchableFilterProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState('')
  const debouncedSearch = useDebounce(searchQuery, 400)

  // Track if there is active search
  const hasSearch = debouncedSearch.trim() !== ''

  // Fetch options
  const { data: options = [], isLoading } = useQuery<SearchableFilterOption[]>({
    queryKey: [name, debouncedSearch],
    queryFn: () => fetcher(debouncedSearch),
    staleTime: 30 * 60 * 1000, // 30 minutes
    enabled: isOpen,
  })

  // Find selected option
  const selectedOption = React.useMemo(() => {
    if (!value) return null
    return options.find((opt) => opt.id === value) || null
  }, [value, options])

  const visibleOptions = React.useMemo(() => {
    return options.filter((opt) => !(opt.id === 'all' && hasSearch))
  }, [options, hasSearch])

  const handleSelect = (option: SearchableFilterOption) => {
    onChange(option.id)
    setIsOpen(false)
    setSearchQuery('')
  }

  const buttonLabel =
    !value || value === 'all' || selectedOption?.id === 'all'
      ? title
      : selectedOption?.label || title

  return (
    <PopoverPrimitive.Root onOpenChange={setIsOpen} open={isOpen}>
      <PopoverPrimitive.Trigger asChild>
        <Button
          className={cn(
            'h-9 min-w-fit shrink-0 justify-start px-3 font-normal text-[#262626]',
            'border-[#D4D4D4] bg-[#FAFAFA] text-sm shadow-[0_1px_2px_rgba(0,0,0,0.05)]',
            'hover:bg-[#F5F5F5] active:bg-[#E5E5E5]'
          )}
          icon={icon}
          iconPosition="left"
          size="sm"
          variant="outline"
        >
          <span className="flex items-center gap-2">
            {buttonLabel}
            <ChevronsUpDown className="h-4 w-4" />
          </span>
        </Button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align={popoverAlign}
          className={cn(
            'z-[9999] flex flex-col',
            'overflow-hidden rounded-[6px] border border-[#D4D4D4] bg-[#FAFAFA]',
            'shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-2px_rgba(0,0,0,0.1)]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95'
          )}
          onOpenAutoFocus={(e) => {
            e.preventDefault()
          }}
          side="bottom"
          sideOffset={4}
          style={{ width: popoverWidth }}
        >
          {/* Search Input - Optional */}
          {!hideSearch && (
            <>
              <div className="p-2">
                <SearchFilter
                  debounceDelay={400}
                  onChange={setSearchQuery}
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                />
              </div>

              {/* Divider */}
              <div className="flex h-px w-full items-center rounded-[2px] bg-[#FAFAFA]">
                <div className="h-px w-full bg-[#D4D4D4]" />
              </div>
            </>
          )}

          {/* Options List */}
          <ul
            className={cn(
              'max-h-[200px] flex-1',
              'overflow-y-auto p-2',
              'scrollbar-thumb-rounded-full scrollbar-thin scrollbar-track-transparent scrollbar-thumb-[#737373]',
              'touch-pan-y'
            )}
            onTouchMove={(event) => event.stopPropagation()}
            onTouchStart={(event) => event.stopPropagation()}
            onWheel={(e) => {
              e.stopPropagation()
            }}
            style={{
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
            }}
          >
            {isLoading ? (
              <li className="flex items-center justify-center px-3 py-8">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#D4D4D4] border-t-[#737373]" />
              </li>
            ) : visibleOptions.length === 0 ? (
              <li className="px-3 py-2 text-center text-sm text-gray-500">
                {emptyMessage}
              </li>
            ) : (
              options.map((option) => {
                const isSelected = value === option.id
                // Only show "All" if there's no search
                if (option.id === 'all' && hasSearch) return null
                return (
                  <li key={option.id}>
                    <button
                      className={cn(
                        'relative flex w-full cursor-pointer select-none items-center',
                        'gap-2 rounded-[6px] px-2 py-1 text-left transition-colors',
                        'min-h-[32px]',
                        option.disabled
                          ? 'cursor-not-allowed bg-[#FAFAFA] text-gray-400'
                          : 'bg-[#FAFAFA] text-[#262626] hover:bg-neutral-100',
                        isSelected && 'bg-neutral-100'
                      )}
                      disabled={option.disabled}
                      onClick={() => !option.disabled && handleSelect(option)}
                      type="button"
                    >
                      <div className="flex flex-1 flex-col justify-center">
                        <span
                          className={cn(
                            'block truncate text-sm font-normal leading-5',
                            'tracking-[0.005em] text-[#262626]',
                            isSelected && 'font-semibold'
                          )}
                        >
                          {option.label}
                        </span>
                        {option.subtitle && (
                          <span className="text-xs text-[#737373]">
                            {option.subtitle}
                          </span>
                        )}
                      </div>

                      {isSelected && (
                        <span className="flex items-center text-[#737373]">
                          <svg
                            className="h-5 w-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              clipRule="evenodd"
                              d={
                                'M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 ' +
                                '0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                              }
                              fillRule="evenodd"
                            />
                          </svg>
                        </span>
                      )}
                    </button>
                  </li>
                )
              })
            )}
          </ul>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}
